'use client'

import { Fragment, useState } from 'react'
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react'
import { ChevronDown, Loader2, MapPinHouse, Pencil, Plus, Star, Trash2, X } from 'lucide-react'
import { toast } from '@/lib/toast'
import AddressEdit from './AddressEdit'
import { formatAddressDetails } from '@/utils/order'
import { useAddressForm, useAddressManagement } from '@/lib/commerce/hooks'

const TYPE_LABELS: Record<string, string> = {
  apartment: 'Орон сууц',
  house: 'Хашаа байшин',
  office: 'Оффис',
}

const inputStyle: React.CSSProperties = {
  background: 'rgba(0,0,0,0.3)',
  border: '1px solid rgba(255,255,255,0.07)',
  color: 'white',
}

const labelClass = 'block text-[10px] font-semibold uppercase tracking-[0.18em] mb-1.5'
const labelStyle: React.CSSProperties = { color: '#a8a8b3' }

function DarkInput({ label, value, onChange, placeholder, required, type, description }: {
  label: string
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  placeholder?: string
  required?: boolean
  type?: string
  description?: string
}) {
  return (
    <label className="block">
      <div className={labelClass} style={labelStyle}>
        {label}{required && <span className="ml-1" style={{ color: '#fda4af' }}>*</span>}
      </div>
      <input
        type={type || 'text'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full h-11 rounded-lg px-3.5 text-[13px] outline-none focus:ring-0 placeholder:text-white/30"
        style={inputStyle}
      />
      {description && (
        <div className="mt-1.5 text-[11px]" style={{ color: 'rgba(255,255,255,0.4)' }}>{description}</div>
      )}
    </label>
  )
}

function DarkSelect({ label, value, onChange, items, required, placeholder }: {
  label: string
  value: string | null
  onChange: (v: string) => void
  items: { value: string, label: string }[]
  required?: boolean
  placeholder?: string
}) {
  return (
    <label className="block">
      <div className={labelClass} style={labelStyle}>
        {label}{required && <span className="ml-1" style={{ color: '#fda4af' }}>*</span>}
      </div>
      <div className="relative">
        <select
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-11 rounded-lg pl-3.5 pr-9 text-[13px] outline-none focus:ring-0 appearance-none"
          style={{ ...inputStyle, color: value ? 'white' : 'rgba(255,255,255,0.3)' }}
        >
          <option value="" disabled style={{ background: '#0b0b0e' }}>{placeholder || `${label} сонгох`}</option>
          {items.map(item => (
            <option key={item.value} value={item.value} style={{ background: '#0b0b0e', color: 'white' }}>{item.label}</option>
          ))}
        </select>
        <ChevronDown className="h-3.5 w-3.5 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'rgba(255,255,255,0.5)' }} />
      </div>
    </label>
  )
}

function TypePills({ value, onChange }: { value: string, onChange: (v: any) => void }) {
  const types: { key: 'apartment' | 'house' | 'office', label: string }[] = [
    { key: 'apartment', label: 'Орон сууц' },
    { key: 'house', label: 'Хашаа байшин' },
    { key: 'office', label: 'Оффис' },
  ]
  return (
    <div className="flex items-center gap-1 p-1 rounded-full" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.07)' }}>
      {types.map(t => {
        const active = value === t.key
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => onChange(t.key)}
            className="flex-1 h-9 rounded-full text-[12px] font-medium transition-colors cursor-pointer"
            style={{
              background: active ? 'white' : 'transparent',
              color: active ? '#0a0a0c' : 'rgba(255,255,255,0.7)',
            }}
          >
            {t.label}
          </button>
        )
      })}
    </div>
  )
}

export function DarkAddressFormFields({ form }: { form: ReturnType<typeof useAddressForm> }) {
  return (
    <div className="flex flex-col gap-4">
      <TypePills value={form.addressType} onChange={form.setAddressType} />
      <DarkSelect label="Хот / Аймаг" value={form.selectedProvince} items={form.provinces} onChange={form.onProvinceChange} required />
      <DarkSelect label="Дүүрэг / Сум" value={form.selectedDistrict} items={form.districts} onChange={form.onDistrictChange} required />
      <DarkSelect label="Хороо / Баг" value={form.selectedSubdistrict} items={form.subdistricts} onChange={form.onSubdistrictChange} required />
      {form.addressType === 'apartment' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <DarkInput label="Байр" value={form.bair} onChange={(e) => form.setBair(e.target.value)} placeholder="Байрны дугаар" required />
          <DarkInput label="Тоот" value={form.toot} onChange={(e) => form.setToot(e.target.value)} placeholder="Хаалганы тоот" required />
        </div>
      )}
      {form.addressType === 'house' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <DarkInput label="Хашааны дугаар" value={form.houseNum} onChange={(e) => form.setHouseNum(e.target.value)} placeholder="Хашааны дугаар" required />
          <DarkInput label="Гудамж" value={form.street} onChange={(e) => form.setStreet(e.target.value)} placeholder="Гудамжны нэр" />
        </div>
      )}
      {form.addressType === 'office' && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <DarkInput label="Барилга" value={form.building} onChange={(e) => form.setBuilding(e.target.value)} placeholder="Барилгын нэр/дугаар" required />
            <DarkInput label="Давхар" value={form.floor} onChange={(e) => form.setFloor(e.target.value)} placeholder="Давхар" required />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <DarkInput label="Тоот" value={form.toot} onChange={(e) => form.setToot(e.target.value)} placeholder="Хаалганы тоот" required />
            <DarkInput label="Оффисын нэр" value={form.officeName} onChange={(e) => form.setOfficeName(e.target.value)} placeholder="Заавал биш" />
          </div>
        </>
      )}
      <DarkInput
        label="Нэмэлт мэдээлэл"
        value={form.additionalAddress}
        onChange={(e) => form.setAdditionalAddress(e.target.value)}
        placeholder="Орц, давхар, орцны код гэх мэт"
        description="Хүргэлтийн ажилтанд туслах нэмэлт мэдээлэл"
      />
    </div>
  )
}

export function DarkAddressModal({ open, onClose, title, onSave, submitting, children }: {
  open: boolean
  onClose: () => void
  title: string
  onSave: () => void
  submitting: boolean
  children: React.ReactNode
}) {
  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild as={Fragment} enter="transition-opacity ease-linear duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="transition-opacity ease-linear duration-300" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/70" />
        </TransitionChild>
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <TransitionChild as={Fragment} enter="transition ease-out duration-300" enterFrom="opacity-0 translate-y-4 sm:scale-95" enterTo="opacity-100 translate-y-0 sm:scale-100" leave="transition ease-in duration-200" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-4 sm:scale-95">
            <DialogPanel
              className="relative flex flex-col w-full sm:max-w-lg max-h-[90vh] rounded-t-3xl sm:rounded-3xl overflow-hidden"
              style={{ background: '#0b0b0e', border: '1px solid rgba(255,255,255,0.13)', boxShadow: '0 30px 80px -20px rgba(0,0,0,0.7)' }}
            >
              <div className="px-6 pt-5 pb-4 flex items-center justify-between shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                <div className="text-[15px] font-semibold text-white" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>{title}</div>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Хаах"
                  className="w-9 h-9 inline-flex items-center justify-center rounded-full transition-colors hover:bg-white/5 cursor-pointer"
                  style={{ color: 'rgba(255,255,255,0.6)' }}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto overscroll-contain scrollbar-dark px-6 py-5">
                {children}
              </div>
              <div className="px-6 py-4 shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                <button
                  type="button"
                  onClick={onSave}
                  disabled={submitting}
                  className="w-full h-12 rounded-full text-[13.5px] font-semibold inline-flex items-center justify-center gap-2 transition-opacity disabled:opacity-60 cursor-pointer"
                  style={{ background: 'white', color: '#0a0a0c' }}
                >
                  {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Хадгалж байна…</> : 'Хадгалах'}
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  )
}

export default function AddressList({ addressList, addressField, vendor }: { addressList: any, addressField: any, vendor: any }) {
  const addresses: any[] = addressList || []
  const isLoading = addressList === undefined || addressField === undefined
  const form = useAddressForm({ addressField, vendor })
  const mgmt = useAddressManagement()

  const [openAdd, setOpenAddLocal] = useState(false)

  const handleAdd = async () => {
    const v = form.validate()
    if (!v.ok) return toast.error(v.error)
    const ok = await mgmt.addAddress(form.compose())
    if (ok) {
      mgmt.setOpenAdd(false)
      setOpenAddLocal(false)
      form.reset()
    }
  }

  const handleOpenAdd = () => {
    mgmt.setOpenAdd(true)
    setOpenAddLocal(true)
  }
  const handleCloseAdd = () => {
    mgmt.setOpenAdd(false)
    setOpenAddLocal(false)
  }

  return (
    <>
      <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pt-10 pb-16">
        <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
          <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#a8a8b3' }}>
            Хадгалсан хаягууд{addresses.length > 0 ? ` · ${addresses.length}` : ''}
          </div>
          <button
            onClick={handleOpenAdd}
            className="h-9 px-5 rounded-full text-[12.5px] font-semibold inline-flex items-center gap-2 cursor-pointer"
            style={{ background: 'white', color: '#0a0a0c' }}
          >
            <Plus className="h-3.5 w-3.5" /> Шинэ хаяг
          </button>
        </div>

        {isLoading ? (
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="py-7" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                <div className="h-3 w-32 rounded animate-pulse mb-2" style={{ background: 'rgba(255,255,255,0.04)' }} />
                <div className="h-4 w-72 rounded animate-pulse" style={{ background: 'rgba(255,255,255,0.06)' }} />
              </div>
            ))}
          </div>
        ) : addresses.length > 0 ? (
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
            {addresses.map((a: any, i: number) => {
              const isDefaulting = mgmt.defaultingId === a.id
              const isDeleting = mgmt.deletingId === a.id
              const rowBusy = isDefaulting || isDeleting
              return (
                <div
                  key={a.id}
                  className="grid grid-cols-12 gap-4 py-7 items-start"
                  style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}
                >
                  <div className="col-span-12 sm:col-span-3">
                    <div className="text-[11px] font-mono tabular-nums tracking-[0.16em]" style={{ color: 'rgba(255,255,255,0.4)' }}>
                      {String(i + 1).padStart(2, '0')}
                    </div>
                    <div className="mt-2 inline-flex items-center gap-2 flex-wrap">
                      <span
                        className="text-[16px] sm:text-[18px] font-semibold tracking-[-0.01em] text-white"
                        style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}
                      >
                        {TYPE_LABELS[a.type || 'apartment'] || 'Хаяг'}
                      </span>
                      {a.isDefault && (
                        <span
                          className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9.5px] font-semibold uppercase tracking-[0.16em]"
                          style={{ background: 'white', color: '#0a0a0c' }}
                        >
                          Үндсэн
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="col-span-12 sm:col-span-6 min-w-0">
                    <div className="text-[14px] sm:text-[15px] text-white">
                      {[a.city, a.duureg, a.khoroo].filter(Boolean).join(', ')}
                    </div>
                    <div className="text-[13px] mt-1.5 leading-6" style={{ color: 'rgba(255,255,255,0.6)' }}>
                      {formatAddressDetails(a)}
                    </div>
                    {a.additionalAddress && (
                      <div className="text-[12px] mt-1" style={{ color: 'rgba(255,255,255,0.45)' }}>
                        {a.additionalAddress}
                      </div>
                    )}
                  </div>
                  <div className="col-span-12 sm:col-span-3 sm:text-right inline-flex sm:justify-end items-center gap-2 flex-wrap">
                    {!a.isDefault && (
                      <button
                        onClick={() => !rowBusy && mgmt.setDefault(a.id)}
                        disabled={rowBusy}
                        className="h-9 px-4 rounded-full text-[12px] font-medium inline-flex items-center gap-1.5 transition-colors hover:bg-white/5 disabled:opacity-50 cursor-pointer"
                        style={{ background: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.07)' }}
                      >
                        {isDefaulting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Star className="h-3.5 w-3.5" />}
                        <span className="hidden sm:inline">Үндсэн</span>
                      </button>
                    )}
                    <button
                      onClick={() => !rowBusy && mgmt.startEdit(a)}
                      disabled={rowBusy}
                      className="h-9 px-4 rounded-full text-[12px] font-medium inline-flex items-center gap-1.5 transition-colors hover:bg-white/5 disabled:opacity-50 cursor-pointer"
                      style={{ background: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.07)' }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">Засах</span>
                    </button>
                    <button
                      onClick={() => !rowBusy && mgmt.removeAddress(a.id)}
                      disabled={rowBusy}
                      aria-label="Устгах"
                      className="h-9 w-9 rounded-full inline-flex items-center justify-center transition-colors hover:bg-rose-500/10 disabled:opacity-50 cursor-pointer"
                      style={{ background: 'transparent', color: '#fda4af', border: '1px solid rgba(244,63,94,0.3)' }}
                    >
                      {isDeleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-3 py-24" style={{ color: 'rgba(255,255,255,0.5)' }}>
            <MapPinHouse className="h-8 w-8" />
            <div className="text-[14px]">Хаяг байхгүй байна</div>
          </div>
        )}
      </div>

      <DarkAddressModal
        open={openAdd || mgmt.openAdd}
        onClose={handleCloseAdd}
        title="Шинэ хаяг оруулах"
        onSave={handleAdd}
        submitting={mgmt.submitting}
      >
        <DarkAddressFormFields form={form} />
      </DarkAddressModal>

      <AddressEdit
        open={mgmt.openEdit}
        setOpen={mgmt.setOpenEdit}
        address={mgmt.addressToEdit}
        addressField={addressField}
        vendor={vendor}
        updateAddress={mgmt.updateAddress}
        submitting={mgmt.submitting}
      />
    </>
  )
}
